import {
  FieldValidationLevel,
  SettleValueTypeEnum,
  toBigWithDefault,
  validatePrecision,
  type Currency,
  type FieldValidationResult,
  type Security,
} from '@talos/kyoko';
import type { Big } from 'big.js';

/**
 * Resolves the effective quantity minimums for the currently selected order currency.
 *
 * Quantity can be entered in contracts, base currency, or quote/counter currency depending
 * on the product. Base/contract quantities use the security size increment, while quote
 * quantities use the currency increment. Inverted products flip which side behaves like
 * a currency amount.
 */
export const getQuantityValidationLimits = (
  security: Security,
  orderCurrency: string,
  currenciesBySymbol: Map<string, Currency>
): { minSize: string; minIncrement: string } => {
  const orderCurrencyInfo = currenciesBySymbol.get(orderCurrency);
  const { MinimumSize, MinSizeIncrement } = security;
  const { MinIncrement } = orderCurrencyInfo || {};

  if (!orderCurrency) {
    // No order currency means the quantity is a contract count.
    return {
      minSize: toBigWithDefault(MinimumSize, 1).toFixed(),
      minIncrement: toBigWithDefault(MinSizeIncrement, 1).toFixed(),
    };
  }

  if (security.SettleValueType === SettleValueTypeEnum.Inverted) {
    if (orderCurrency === security.BaseCurrency) {
      // Inverted base-currency orders behave like currency amounts.
      return {
        minSize: MinIncrement ?? '0.01',
        minIncrement: MinIncrement ?? '0.01',
      };
    }

    // Inverted quote-currency orders behave like contract/size amounts.
    return {
      minSize: toBigWithDefault(MinimumSize, 1)
        .mul(security.NotionalMultiplier ?? 1)
        .toFixed(),
      minIncrement: toBigWithDefault(MinSizeIncrement, 1)
        .mul(security.NotionalMultiplier ?? 1)
        .toFixed(),
    };
  }

  if (orderCurrency === security.BaseCurrency) {
    // Regular base-currency orders use the security's size rules, adjusted for contract multiplier.
    return {
      minSize: toBigWithDefault(MinimumSize, 1)
        .mul(security.NotionalMultiplier ?? 1)
        .toFixed(),
      minIncrement: toBigWithDefault(MinSizeIncrement, 1)
        .mul(security.NotionalMultiplier ?? 1)
        .toFixed(),
    };
  }

  // Regular quote/counter-currency orders use the selected currency's amount increment.
  return {
    minSize: MinIncrement ?? '0.01',
    minIncrement: MinIncrement ?? '0.01',
  };
};

// https://talostrading.atlassian.net/browse/UI-4444
/**
 * Validates order quantity against the effective minimum size and increment for the
 * selected security/currency combination. This intentionally shares the limit resolver
 * used by resubmit-remaining normalization so a value inserted into the form is checked
 * with the same rules the user sees while editing.
 */
export const validateQuantity = (
  security: Security,
  quantity: Big,
  orderCurrency: string,
  currenciesBySymbol: Map<string, Currency>
): FieldValidationResult | null => {
  const { minSize, minIncrement } = getQuantityValidationLimits(security, orderCurrency, currenciesBySymbol);

  if (!orderCurrency) {
    // Contracts
    if (quantity.lt(minSize)) {
      return {
        message: `Min contract is ${minSize}`,
        level: FieldValidationLevel.Error,
      };
    }

    if (!validatePrecision(minIncrement, quantity)) {
      return {
        message: `Min increment is ${minIncrement}`,
        level: FieldValidationLevel.Error,
      };
    }
  } else {
    if (quantity.lt(minSize)) {
      return {
        message: `Min quantity is ${minSize}`,
        level: FieldValidationLevel.Error,
      };
    }

    if (!validatePrecision(minIncrement, quantity)) {
      return {
        message: `Min increment is ${minIncrement}`,
        level: FieldValidationLevel.Error,
      };
    }
  }

  return null;
};
